import { t } from 'i18next';
import { CurriculaListQuery, TeachingLoadFactorsQuery } from '../../../types/planung-graphql-client-defs';
import { groupByArray, partition } from '../../../utils/arrayFunc';
import { calculateLessonInfos, DeputatInfo, TimetableInfo } from '../../../utils/calculateLessonInfos';
import { CardsInfo } from '../../Lessons/TimetableVersion/Table/TimetableVersionLessonsTable';
import { CurriculaTableDataType } from '../types';

export const curriculumResponseToTableType = ({
  curriculumVersionUuid,
  teachingLoadFactors,
  queryCurriculaData,
  schoolYearUuid,
  filterByGrade,
  filterBySubject,
}: {
  curriculumVersionUuid: string | undefined;
  queryCurriculaData: CurriculaListQuery | undefined;
  teachingLoadFactors?: TeachingLoadFactorsQuery | undefined;
  schoolYearUuid?: string;
  filterByGrade?: string;
  filterBySubject?: string;
}): CurriculaTableDataType[] => {
  return (
    queryCurriculaData?.curricula
      .filter((curriculum) => {
        const isGradeFiltered =
          !curriculum.curriculumGrades.length ||
          curriculum.curriculumGrades.some((cg) => cg.grades.includes(parseInt(filterByGrade as string)));
        const isSubjectFiltered = filterBySubject && curriculum.subject.uuid === filterBySubject;

        return isGradeFiltered || isSubjectFiltered || (!filterByGrade && !filterBySubject);
      })
      .map((curriculum) => {
        const data: CurriculaTableDataType = {
          isSubjectContainerLesson: curriculum.subject.isSubjectContainer,
          subject: curriculum.subject.name,
          subjectUuid: curriculum.subject.uuid,
          subjectShortName: curriculum.subject.shortName,
          uuid: curriculum.uuid,
          comment: curriculum.comment,
          subTitle: curriculum.subTitle,
          elective: curriculum.elective ?? false,
          teachingLoadEnabled: curriculum.teachingLoadEnabled ?? false,
          timetableEnabled: curriculum.timetableEnabled ?? false,
          teachers: curriculum.persons.map((person) => ({
            order: person.order,
            description: person.description,
            teachingLoadEnabled: !!person.teachingLoad,
            presenceEnabled: !!person.present,
          })),
          teacherUuids: curriculum.persons.map((p) => p.uuid),
          curriculumVersionUuid: curriculumVersionUuid ?? '',
          grades: curriculum.curriculumGrades.flatMap((cg) => cg.grades),
          classes: curriculum.curriculumGrades.flatMap((cg) => {
            return cg.plannedClasses.map((planned) => ({
              uuid: planned.name,
              name: planned.name,
              shortName: planned.shortName,
              grade: planned.grade,
              gradeGroup: planned.gradeGroup,
              groups: planned.groups.map((g) => ({
                uuid: g.uuid,
                name: g.name,
                shortName: g.shortName,
                classFraction: cg.groups.find((group) => group.uuid === g.uuid)?.classFraction ?? 1,
              })),
            }));
          }),
          lessonUnits:
            curriculum.curriculumUnits.map((cu) => {
              return {
                uuid: cu.uuid,
                duration: cu.duration ?? 0,
                count: cu.count ?? 0,
                subjectContainer: cu.subjectContainer,
              };
            }) ?? [],
          curriculumGrades: curriculum.curriculumGrades,
          groupGradeNames: curriculum.groupGradeNames,
          teachingLoadFactors: teachingLoadFactors?.teachingLoadFactors.map((t) => t.value.toFixed(2)).join('/') ?? '',
        };

        const colors = curriculum.labels?.map((label) => label.color ?? '#fff') ?? ['#fff'];
        // Je geplanter Zuordnung der Sollstunde eine Berechnung; Schalter und Einheiten sind die der Sollstunde.
        const lessonInfo = curriculum.plannedLessons
          .filter((lesson) =>
            lesson.classes.some(
              (planned) => !filterByGrade || planned.grade === (filterByGrade ? parseInt(filterByGrade, 10) : null),
            ),
          )
          .map((lesson) =>
            calculateLessonInfos(
              {
                uuid: lesson.uuid,
                subject: curriculum.subject.name,
                subjectShortName: curriculum.subject.shortName,
                subjectUuid: curriculum.subject.uuid,
                classes: lesson.classes.map((planned) => ({
                  uuid: `uuid_${planned.grade}`,
                  name: planned.name,
                  shortName: planned.shortName,
                  grade: planned.grade,
                  gradeGroup: planned.gradeGroup,
                  groups: planned.groups.map((g) => ({
                    uuid: g.uuid,
                    name: g.name,
                    shortName: g.shortName,
                    // OFFENE ENTSCHEIDUNG (Stand 2026-10-10, vorerst so belassen): In der Deputats- und
                    // Stundenberechnung der Sollstunden zählt eine Gruppe als ganze Klasse (Anteil 1), so wie es
                    // der frühere Resolver lieferte. Der echte Anteil (eins durch die Zahl der Gruppen der
                    // Teilung) steht in `curriculumGrades[].groups.classFraction`. Wer umstellt, ändert die
                    // Zahlen in allen Sollstunden-Tabellen. Siehe Befund 107 im Backend-Plan
                    // (docs/plans/2026-10-07-custom-resolver-migration-plan.md).
                    classFraction: 1,
                  })),
                })),
                teachers: curriculum.persons.map((p) => ({
                  uuid: p.uuid,
                  name: p.name,
                  color: '',
                  teachingLoadEnabled: p.teachingLoad ?? true,
                  presenceEnabled: p.present ?? true,
                })),
                teachingLoadEnabled: curriculum.teachingLoadEnabled ?? true,
                timetableEnabled: curriculum.timetableEnabled ?? true,
                lessonUnits: curriculum.curriculumUnits.map((lu) => ({
                  ...lu,
                  duration: lu.duration ?? 0,
                  count: lu.count ?? 0,
                })),
                isEpochPlan: curriculum.subject.isSubjectContainer,
                curriculumUuid: curriculum.uuid,
              },
              teachingLoadFactors,
              ['BASE', 'TIME'],
              true,
            ),
          );

        data.lessonInfo = lessonInfo;

        const deputat =
          lessonInfo.map((i) => i.deputat).reduce((a, b) => a + b, 0.0) *
          curriculum.persons.filter((p) => p.teachingLoad).length;

        data.curriculumInfo = {
          uuid: curriculum.uuid,
          partialClasses: false,
          teachingLoadEnabled: curriculum.teachingLoadEnabled ?? false,
          teachersCount: lessonInfo.map((i) => i.teachersCount).reduce((a, b) => a + b, 0),
          teachersCountTeachingLoadEnabled: lessonInfo
            .map((i) => i.teachersCountTeachingLoadEnabled)
            .reduce((a, b) => a + b, 0),
          classesCount: lessonInfo.map((i) => i.classesCount).reduce((a, b) => a + b, 0),

          subjectContainerUuids: [],
          subjectContainerTeacherInfos: new Map<string, Map<string, DeputatInfo>>(),
          subjectContainerClassInfos: new Map<string, Map<string, DeputatInfo>>(),
          teacherInfos: lessonInfo
            .map((i) => i.classInfos)
            .reduce((a, b) => {
              b &&
                b.forEach((v, k) => {
                  a.set(k, {
                    deputat: (a.get(k)?.deputat ?? 0) + v.deputat,
                    fachstundenDeputat: (a.get(k)?.fachstundenDeputat ?? 0) + (v.fachstundenDeputat ?? 0),
                    epochenDeputat: (a.get(k)?.epochenDeputat ?? 0) + (v.epochenDeputat ?? 0),
                  });
                });
              return a;
            }, new Map<string, DeputatInfo>()),
          classInfos: lessonInfo
            .map((i) => i.classInfos)
            .reduce((a, b) => {
              b.forEach((v, k) => {
                a.set(k, {
                  studentCards: (a.get(k)?.studentCards ?? 0.0) + (v.studentCards ?? 0.0),
                  studentHours: (a.get(k)?.studentHours ?? 0.0) + (v.studentHours ?? 0.0),

                  teacherCards: (a.get(k)?.teacherCards ?? 0.0) + (v.teacherCards ?? 0.0),
                  teacherHours: (a.get(k)?.teacherHours ?? 0.0) + (v.teacherHours ?? 0.0),

                  teachingBlockWeeks: (a.get(k)?.teachingBlockWeeks ?? 0.0) + (v.teachingBlockWeeks ?? 0.0),
                  teachingBlocks: (a.get(k)?.teachingBlocks ?? 0.0) + (v.teachingBlocks ?? 0.0),

                  deputat: (a.get(k)?.deputat ?? 0.0) + v.deputat,
                  fachstundenDeputat: (a.get(k)?.fachstundenDeputat ?? 0.0) + (v.fachstundenDeputat ?? 0.0),
                  epochenDeputat: (a.get(k)?.epochenDeputat ?? 0.0) + (v.epochenDeputat ?? 0.0),
                });
              });
              return a;
            }, new Map<string, TimetableInfo & DeputatInfo>()),

          studentCards: lessonInfo.map((i) => i.studentCards).reduce((a, b) => a + b, 0.0),
          studentHours: lessonInfo.map((i) => i.studentHours).reduce((a, b) => a + b, 0.0),

          teacherCards: lessonInfo.map((i) => i.teacherCards).reduce((a, b) => a + b, 0.0),
          teacherHours: lessonInfo.map((i) => i.teacherHours).reduce((a, b) => a + b, 0.0),

          teachingBlockWeeks: lessonInfo.map((i) => i.teachingBlockWeeks).reduce((a, b) => a + b, 0.0),
          teachingBlocks: lessonInfo.map((i) => i.teachingBlocks).reduce((a, b) => a + b, 0.0),

          deputat: deputat,
          fachstundenDeputat: lessonInfo.map((i) => i.fachstundenDeputat ?? 0.0).reduce((a, b) => a + b, 0.0),
          epochenDeputat: lessonInfo.map((i) => i.epochenDeputat ?? 0.0).reduce((a, b) => a + b, 0.0),
        };

        const [subjectContainers, defaultLessons] = partition(curriculum.curriculumUnits, (e) => !!e.subjectContainer);

        const subjectContainersDurationGroups = groupByArray(
          subjectContainers,
          (curriculumUnit) => curriculumUnit.duration ?? 1,
        );
        const defaultLessonsDurationGroups = groupByArray(defaultLessons, (cu) => cu.duration ?? 1);

        const defaultLessonsCardData = defaultLessonsDurationGroups.map((group) => {
          const count = group
            .map((g) => g.count)
            .reduce((previousValue, currentValue) => {
              return (previousValue ?? 0) + (currentValue ?? 0);
            }, 0);

          return group.map((curriculumUnit) => {
            const card: CardsInfo = {
              uuid: `${curriculumUnit.uuid}-${curriculumUnit.duration}-${curriculum.subject?.uuid}`,
              type: 'FS',
              typeLabel: t('FS'),
              typeLabelShort: t('FS'),
              cardLabel: curriculum.subject?.name,
              colors,
              counter: count ?? 1,
              cardLabelShort: curriculum.subject?.shortName,
              duration: curriculumUnit.duration ?? 1,
            };
            return card;
          });
        });

        const subjectContainersCardData = subjectContainersDurationGroups.map((group) => {
          const count = group
            .map((g) => g.count)
            .reduce((previousValue, currentValue) => {
              return (previousValue ?? 0) + (currentValue ?? 0);
            }, 0);
          return group.map((curriculumUnit) => {
            const card: CardsInfo = {
              uuid: `${curriculumUnit.uuid}-${curriculumUnit.duration}-${curriculum.subject?.uuid}`,
              type: 'Container',
              typeLabel: curriculumUnit.subjectContainer?.name ?? '',
              typeLabelShort: curriculumUnit.subjectContainer?.shortName ?? '',
              cardLabel: `${curriculumUnit.duration} ${t('common.week', { count: curriculumUnit.duration ?? 1 })}`,
              cardLabelShort: `${curriculumUnit.duration} ${t('common.week', { count: curriculumUnit.duration ?? 1 })}`,
              colors,
              counter: count ?? 1,
              duration: curriculumUnit.duration ?? 1,
            };
            return card;
          });
        });

        data.cardsInfos = { defaultCards: defaultLessonsCardData, subjectContainerCards: subjectContainersCardData };

        return data;
      }) ?? []
  );
};
